<template>
  <section
    v-show="visible"
    class="home-kanban-panel"
    :class="`home-kanban-panel--${column.tone}`"
    :data-panel-id="column.id"
  >
    <HomeKanbanSourceLoader
      v-for="src in column.sources"
      :key="`${src.key}-${refreshToken}`"
      :source="src"
      :search="search"
      @update="onSourceUpdate"
    />

    <div
      ref="chipsEl"
      class="home-kanban-panel__chips"
    >
      <button
        v-for="src in column.sources"
        :key="src.key"
        type="button"
        class="home-kanban-panel__chip"
        :class="{ 'home-kanban-panel__chip--active': activeSources[src.key] }"
        @click="toggleSource(src.key)"
      >
        {{ src.label || t(src.labelKey) }}
      </button>
    </div>

    <div
      class="home-kanban-panel__body"
      :class="bodyLayoutClass"
    >
      <div
        v-if="pending && !visibleItems.length"
        class="home-kanban-panel__loading"
      >
        {{ t('loading') }}
      </div>
      <div
        v-else-if="!visibleItems.length"
        class="home-kanban-panel__empty"
      >
        {{ t('empty') }}
      </div>
      <HomeKanbanCard
        v-for="item in visibleItems"
        :key="`${item.sourceKey}-${item.row.id}`"
        :title="cardTitle(item.row)"
        :tag="item.source.label || t(item.source.labelKey)"
        :tone="item.source.tagTone || column.tone"
        :rows="cardRows(item)"
        :selected="selectedId != null && Number(item.row.id) === Number(selectedId)"
        @select="emit('select', { id: Number(item.row.id), category: item.source.category, row: item.row })"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { productDisplayName } from '@/lib/productCard'
import { formatHomeDateDisplay } from '@/lib/dateInput'
import { productMatchesBu } from './columns'
import HomeKanbanCard from './HomeKanbanCard.vue'
import HomeKanbanSourceLoader from './HomeKanbanSourceLoader.vue'

const props = defineProps({
  column: { type: Object, required: true },
  visible: { type: Boolean, default: true },
  search: { type: String, default: '' },
  bu: { type: String, default: 'all' },
  selectedId: { type: Number, default: null },
  expandedCount: { type: Number, default: 1 },
  resetToken: { type: Number, default: 0 },
  /** Смена ключа → remount loader'ов и повторный запрос products. */
  refreshToken: { type: Number, default: 0 },
})

const emit = defineEmits(['select', 'total', 'chips-el'])

const { t, locale } = useI18n()
const preferEnglish = computed(() => locale.value === 'en')
const chipsEl = ref(null)

const activeSources = reactive(
  Object.fromEntries(props.column.sources.map(s => [s.key, true]))
)

const sourceData = reactive({})

watch(
  () => props.resetToken,
  () => {
    props.column.sources.forEach(s => {
      activeSources[s.key] = true
    })
  }
)

watch(
  () => props.refreshToken,
  (token, prev) => {
    if (token === prev) return
    // Убрать устаревшие строки до ответа API, иначе карточка «залипает» в старом блоке.
    props.column.sources.forEach(s => {
      delete sourceData[s.key]
    })
  }
)

watch(
  chipsEl,
  el => emit('chips-el', { id: props.column.id, el }),
  { immediate: true }
)

function onSourceUpdate(payload) {
  sourceData[payload.key] = payload
}

const pending = computed(() =>
  props.column.sources.some(s => sourceData[s.key]?.pending)
)

const allItems = computed(() => {
  const out = []
  for (const src of props.column.sources) {
    const pack = sourceData[src.key]
    if (!pack) continue
    for (const row of pack.rows) {
      out.push({ source: src, sourceKey: src.key, row })
    }
  }
  return out
})

const buFiltered = computed(() =>
  allItems.value.filter(item => productMatchesBu(item.row, props.bu))
)

const visibleItems = computed(() => {
  // Submitted New MAA включает Planned — при обоих чипах не дублируем карточку.
  const seen = new Set()
  const out = []
  for (const item of buFiltered.value) {
    if (!activeSources[item.sourceKey]) continue
    const id = Number(item.row?.id)
    if (Number.isFinite(id) && seen.has(id)) continue
    if (Number.isFinite(id)) seen.add(id)
    out.push(item)
  }
  return out
})

watch(
  buFiltered,
  list => emit('total', list.length),
  { immediate: true }
)

function toggleSource(key) {
  activeSources[key] = !activeSources[key]
}

function cardTitle(row) {
  return productDisplayName(row, preferEnglish.value) || String(row.id)
}

function formatDateValue(value) {
  if (value == null || value === '') {
    return '—'
  }
  return formatHomeDateDisplay(value, { empty: '—' })
}

function cardRows(item) {
  const mode = item.source.dateMode
  const row = item.row
  if (mode === 'none') {
    return []
  }
  if (mode === 'registration') {
    return [
      {
        label: t('rows.submissionDate'),
        value: formatDateValue(row.initial_registration_date || row.new_maa_submission_date),
      },
      {
        label: t('rows.baseCase'),
        value: formatDateValue(row.rc_issue_date_en || row.rc_issue_date_ru),
      },
    ]
  }
  if (mode === 'variation') {
    return [
      {
        label: t('rows.submissionDate'),
        value: formatDateValue(row.initial_registration_date || row.new_maa_submission_date),
      },
      {
        label: t('rows.approvalDate'),
        value: formatDateValue(row.rc_issue_date_en || row.rc_issue_date_ru),
      },
    ]
  }
  return [
    {
      label: t('rows.plannedSubmission'),
      value: formatDateValue(row.new_maa_submission_date || row.card_smoh?.planned_submission_date),
    },
    {
      label: t('rows.plannedApproval'),
      value: formatDateValue(
        row.card_smoh?.planned_approval_date ||
          row.rc_issue_date_en ||
          row.rc_issue_date_ru
      ),
    },
  ]
}

const bodyLayoutClass = computed(() => {
  const n = props.expandedCount
  if (n <= 1) return 'home-kanban-panel__body--cols-4'
  if (n === 2) return 'home-kanban-panel__body--cols-3'
  return 'home-kanban-panel__body--cols-2'
})

function onResize() {
  emit('chips-el', { id: props.column.id, el: chipsEl.value })
}

onMounted(() => window.addEventListener('resize', onResize))
onUnmounted(() => window.removeEventListener('resize', onResize))
</script>

<i18n>
{
  "ru": {
    "loading": "Загрузка…",
    "empty": "Нет записей",
    "pipeline": {
      "newMaa": "Planned New MAA",
      "submittedNewMaa": "Submitted New MAA",
      "newIndications": "New Indication",
      "variation": "Variation",
      "inRegistration": "In Registration",
      "registered": "Variation"
    },
    "rows": {
      "plannedSubmission": "Planned Submission",
      "plannedApproval": "Planned approval",
      "submissionDate": "Submission date",
      "baseCase": "Base case",
      "approvalDate": "Approval Date"
    }
  },
  "en": {
    "loading": "Loading…",
    "empty": "No records",
    "pipeline": {
      "newMaa": "Planned New MAA",
      "submittedNewMaa": "Submitted New MAA",
      "newIndications": "New Indication",
      "variation": "Variation",
      "inRegistration": "In Registration",
      "registered": "Variation"
    },
    "rows": {
      "plannedSubmission": "Planned Submission",
      "plannedApproval": "Planned approval",
      "submissionDate": "Submission date",
      "baseCase": "Base case",
      "approvalDate": "Approval Date"
    }
  }
}
</i18n>
